  import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import {Router} from "@angular/router";
import {Subscription} from "rxjs";
import {finalize} from "rxjs/operators";

import {Dialect, SimpleDialect} from "../../models/dialect.model";
import {GetRegion2Css, GetRegion2Name} from "../../defines/desktop.const";
import {BookmarkChange} from "../../defines/client.const";
import {DialectService} from "../../services/dialect/dialect.service";
import {DialectBookmarkService} from "../../services/dialect/dialect-bookmark.service";
import {GlobalEventService} from "../../services/global-event/global-event.service";
import {DialectHistoryService} from "../../services/dialect/dialect-history.service";
import {DynamicComponentService} from "../../services/dynamic-component/dynamic-component.service";
import {dialectLinkMixin} from "../../mixin/dialect-link.mixin";

@Component({
  selector: 'app-dialect-detail',
  templateUrl: './dialect-detail.component.html',
  styleUrls: ['./dialect-detail.component.scss']
})
export class DialectDetailComponent extends dialectLinkMixin implements OnInit, OnChanges, OnDestroy {
  @HostBinding('style.display') styleBlock = 'block';
  // @Output() loadComplete: EventEmitter<Dialect> = new EventEmitter<Dialect>();
  @Input() dialect_id: string | null = null;
  @Output() bookmarkChange = new EventEmitter<BookmarkChange>();
  @ViewChild('standardWordPopupAnchor', {read: ViewContainerRef}) set standardWordPopupAnchor(
    container: ViewContainerRef | undefined
  ) {
    if (this.standardWordContainer === container) {
      return;
    }
    this.scheduleStandardWordContainerUpdate(container);
  }
  @ViewChild('similarContainer', {read: ViewContainerRef}) similarContainer?: ViewContainerRef;

  protected readonly GetRegion2Name = GetRegion2Name;
  protected readonly GetRegion2Css = GetRegion2Css;

  protected dialect: Dialect | null = null;
  protected similarWords: Array<string> = [];
  protected sameAreaDialects: Array<SimpleDialect> = [];
  protected otherAreaDialects: Array<SimpleDialect> = [];
  protected standardWordContainer?: ViewContainerRef;

  private load$: Subscription | null = null;
  private sendingBookmark = false;
  private standardWordContainerTimerId: ReturnType<typeof setTimeout> | null = null;
  private isDestroyed = false;


  constructor(
    public override router: Router,
    public override dHistorySvc: DialectHistoryService,
    public override dynamicSvc: DynamicComponentService,
    private elementRef: ElementRef,
    private cdr: ChangeDetectorRef,
    private dialectSvc: DialectService,
    protected bmSvc: DialectBookmarkService,
    protected globalEventSvc: GlobalEventService,
  ) {
    super(router, dHistorySvc, dynamicSvc);
  }

  ngOnInit() {
    if (this.dialect_id === null) {
      throw new Error('DialectItemBodyComponent::ngOnInit() id is null');
    }
    this.dialectSvc.GetDetail(this.dialect_id)
      .subscribe(dialect => {
          console.log('DialectDetailComponent::dialectSvc.GetDetail() =', dialect);
          this.setDialect(dialect);
          // this.loadComplete.emit(dialect);
        }, err => console.error(err)
      );
  }

  ngOnChanges(changes: SimpleChanges) {
    const change = changes['dialect_id'];

    if (change && change.previousValue !== change.currentValue) {
      console.warn('DialectDetailComponent::ngOnChanges', this.dialect_id, change.previousValue, change.currentValue);
      this.elementRef.nativeElement.scrollTop = 0;
      this.setDialect(null);

      if (this.dialect_id) {
        this.load$ = this.dialectSvc.GetDetail(this.dialect_id).pipe(
          finalize(() => this.load$ = null)
        ).subscribe(
          data => {
            console.log('DialectDetailComponent::dialectSvc.GetDetail() =', data);
            this.setDialect(data);
          },
          err => console.error(err),
        );
      }
    }
  }

  ngOnDestroy(): void {
    this.isDestroyed = true;
    this.clearStandardWordContainerTimer();
  }

  private scheduleStandardWordContainerUpdate(container: ViewContainerRef | undefined): void {
    this.clearStandardWordContainerTimer();
    this.standardWordContainerTimerId = setTimeout(() => {
      this.standardWordContainerTimerId = null;
      if (this.isDestroyed) {
        return;
      }
      this.standardWordContainer = container;
      this.cdr.markForCheck();
    });
  }

  private clearStandardWordContainerTimer(): void {
    if (this.standardWordContainerTimerId !== null) {
      clearTimeout(this.standardWordContainerTimerId);
      this.standardWordContainerTimerId = null;
    }
  }

  protected ToggleBookmark(event: MouseEvent, dialect: SimpleDialect): void {
    event.preventDefault();
    event.stopPropagation();
    this.globalEventSvc.Click(event, true);

    if (this.sendingBookmark) {
      return; // 이미 보내고 있다.
    }
    this.sendingBookmark = true;
    dialect.bookmark = !dialect.bookmark;
    this.bmSvc.sendBookmark(dialect.id, dialect.bookmark).pipe(
      finalize(() => this.sendingBookmark = false)
    ).subscribe(
      data => {
        console.log(data);
        this.bookmarkChange.emit({id: dialect.id, bookmark: dialect.bookmark});
      },
      err => console.error(err)
    );
  }

  protected getBmCss(dialect: SimpleDialect): string {
    if (this.sendingBookmark) {
      return 'ing';
    }
    return dialect.bookmark ? 'on' : '';
  }

  protected get hasSimilar(): boolean {
    return this.similarWords.length > 0 || this.sameAreaDialects.length > 0;
  }

  protected onClickSimilarDialect(event: MouseEvent, dialect: SimpleDialect): void {
    event.preventDefault();
    event.stopPropagation();
    this.globalEventSvc.Click(event, true);

    if (!this.similarContainer) {
      console.warn('DialectDetailComponent::onClickSimilarDialect() similarContainer is not available');
      return;
    }

    this.OpenDialectLink(dialect.id, this.similarContainer).subscribe(
      result => {
        console.log('DialectDetailComponent::onClickSimilarDialect() result =', result);
      },
      err => console.error('DialectDetailComponent::onClickSimilarDialect() failed', err)
    );
  }

  private setDialect(dialect: Dialect | null): void {
    this.dialect = dialect;
    this.similarWords = [];
    this.sameAreaDialects = [];
    this.otherAreaDialects = [];

    if (!dialect?.dialects) {
      this.similarWords = dialect?.wordMore ?? [];
      return;
    }

    this.sameAreaDialects = dialect.dialects.filter(other => other.area1 === dialect.area1);
    this.otherAreaDialects = dialect.dialects.filter(other => other.area1 !== dialect.area1);

    const sameAreaWords = new Set(this.sameAreaDialects.map(other => other.word?.trim()).filter(Boolean));
    this.similarWords = (dialect.wordMore ?? []).filter(word => !sameAreaWords.has(word.trim()));
  }
}
